import React, { useState, Dispatch, SetStateAction, useCallback, PropsWithChildren } from "react";
import usePromise from "helpers/usePromise";
import staticCache from "helpers/cache/Static";
import { getQueryStringFilters } from "helpers/url";

interface Pager {
    total: number;
    total_page: number;
    last_page: number;
    per_page: number;
    current_page: number;
    order?: string;
    direction?: string;
}

export interface SelectedItemInterface {
    key: "secteur" | "famille";
    label: string;
    value: any;
}

interface ListProviderInterface<T> {
    isLoading: boolean;
    hasError: boolean;
    error?: any;
    data: Array<T> & { pager: Pager } & { active_filters: HashMap<any> } & { warnings?: Array<string> };
    refreshData: () => void;
    setLoading: Dispatch<SetStateAction<boolean>>;
    changeFetcherParams: (params?: any) => any;
    selectedIds: Array<any>;
    setSelectedIds: (selectedIDs: Array<any>) => void;
    selectedId: any;
    setSelectedId: (selectedId: any) => void;
    columns: string[];
    setColumns: (keys: string[]) => void;
    selectedItems: Array<SelectedItemInterface>;
    setSelectedItems: (items: Array<SelectedItemInterface>) => void;
    addSelectedItem: (item: SelectedItemInterface) => void;
    removeSelectedItem: (key: string, value: any) => void;
    activeFetch: boolean;
    setActiveFetch: (value: boolean) => void;
}

const ListContext = React.createContext<ListProviderInterface<any>>({
    isLoading: false,
    hasError: false,
    data: Object.assign([], {
        pager: { total: 0, total_page: 1, last_page: 1, per_page: 50, current_page: 1 },
        active_filters: {},
        warnings: [],
    }),
    refreshData: () => {},
    setLoading: () => {},
    changeFetcherParams: () => {},
    selectedIds: [],
    setSelectedIds: () => {},
    selectedId: undefined,
    setSelectedId: () => {},
    columns: [],
    setColumns: () => {},
    selectedItems: [],
    setSelectedItems: () => {},
    addSelectedItem: () => {},
    removeSelectedItem: () => {},
    activeFetch: true,
    setActiveFetch: () => {},
});

interface Props<T> {
    dataFetcher: (params?: any) => Promise<Array<T> & { pager: Pager; active_filters: HashMap<any> }>;
    defaultFetcherParams?: any;
    defaultColumns?: string[];
    cacheKey?: string;
    defaultFetch?: boolean;
}

const getCache = (cacheKey: string | undefined, propertyName: string, defaultValue?: any) => {
    if (cacheKey && staticCache.has(cacheKey)) {
        const cache = staticCache.get(cacheKey);
        return cache[propertyName] || defaultValue;
    }
    return defaultValue;
};

const setCache = (cacheKey: string | undefined, propertyName: string, value: any) => {
    if (cacheKey) {
        let merge = {};
        if (staticCache.has(cacheKey)) {
            merge = staticCache.get(cacheKey);
        }
        staticCache.set(cacheKey, Object.assign(merge, { [propertyName]: value }));
    }
};

export function ListProvider<T>({
    dataFetcher,
    cacheKey,
    children,
    defaultFetcherParams,
    defaultColumns,
    defaultFetch = true,
}: PropsWithChildren<Props<T>>) {
    const [activeFetch, setInternalActiveFetch] = useState<boolean>(defaultFetch);
    const queryStringFilters = getQueryStringFilters();
    const { isLoading, hasError, error, data, refreshData, changePromiseParams } = usePromise(
        dataFetcher,
        getCache(cacheKey, "fetcherParams", Object.assign({}, defaultFetcherParams, queryStringFilters)),
        Object.assign([], {
            pager: { total: 0, total_page: 1, last_page: 1, per_page: 50, current_page: 1 },
            active_filters: queryStringFilters,
        }),
        activeFetch === false
    );
    const [isLoadingState, setLoading] = useState(false);
    const [selectedIds, setInternalSelectedIds] = useState<Array<any>>(getCache(cacheKey, "selectedIds", []));
    const [selectedId, setSelectedId] = useState<any>(undefined);
    const [columns, setInternalSelectedColumns] = useState<string[]>(defaultColumns || []);
    const [selectedItems, setInternalSelectedItems] = useState<SelectedItemInterface[]>([]);

    const setSelectedIds = useCallback(
        (selectedIds: Array<any>) => {
            setCache(cacheKey, "selectedIds", selectedIds);
            setInternalSelectedIds(selectedIds);
        },
        [setInternalSelectedIds, cacheKey]
    );

    const setColumns = useCallback(
        (selectedColumns: string[]) => {
            setInternalSelectedColumns(selectedColumns);
        },
        [setInternalSelectedColumns]
    );

    const changeFetcherParams = useCallback(
        (params?: any) => {
            const mergeParams = Object.assign({}, params);
            setSelectedIds([]);
            setCache(cacheKey, "fetcherParams", mergeParams);
            changePromiseParams(mergeParams);
        },
        [changePromiseParams, setSelectedIds, cacheKey]
    );

    const setSelectedItems = useCallback(
        (items: Array<SelectedItemInterface>) => {
            if (Array.isArray(items)) {
                setInternalSelectedItems(items);
            }
        },
        [setInternalSelectedItems]
    );

    const addSelectedItem = useCallback(
        (item: SelectedItemInterface) => {
            if (item && selectedItems.findIndex((i: SelectedItemInterface) => i.value === item.value) === -1) {
                selectedItems.push(item);
                const newSelectedItems = [...selectedItems];
                setInternalSelectedItems(newSelectedItems);
            }
        },
        [selectedItems, setInternalSelectedItems]
    );

    const removeSelectedItem = useCallback(
        (key: string, value: any) => {
            setInternalSelectedItems(
                [...selectedItems].filter((i: SelectedItemInterface) => !(i.key === key && i.value === value))
            );
        },
        [selectedItems, setInternalSelectedItems]
    );

    const setActiveFetch = useCallback(
        (value: boolean) => {
            setInternalActiveFetch(value);
        },
        [setInternalActiveFetch]
    );

    return (
        <ListContext.Provider
            value={{
                isLoading: isLoadingState || isLoading,
                hasError,
                error,
                data,
                refreshData,
                setLoading,
                changeFetcherParams,
                selectedIds,
                setSelectedIds,
                selectedId,
                setSelectedId,
                columns,
                setColumns,
                selectedItems,
                setSelectedItems,
                addSelectedItem,
                removeSelectedItem,
                activeFetch,
                setActiveFetch,
            }}
        >
            {children}
        </ListContext.Provider>
    );
}

const Consumer = ListContext.Consumer;

export { Consumer, ListProvider as Provider, ListContext as Context };
